import { Component, OnInit } from '@angular/core';
import { ServerApiService } from '../../../../../server-api/server-api.service';
import { LogService } from '../../../../../services/log/log.service';
import { DocumentRecordsUploadTemplateResult } from '../../../../../models/upload/documents/document-records-upload-template-result';
import { OperationResult } from '../../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import { UtilitiesService } from '../../../../../services/utils/utilities.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
	DocumentUploadContractType,
	DocumentUploadContractTypeExtensions,
} from '../../../../../models/enums/document-upload-contract-type';
import { User } from '../../../../../models/User/user';
import { AppContextService } from '../../../../../services/app-context.service';
import { DocumentsUploadInstructionsModalComponent } from '../instructions-modal/instructions-modal.component';
import { Step } from '../../../../../shared-components/custom-stepper/custom-stepper.component';
import { TranslateService } from '../../../../../services/translate/translate.service';
import { ContractsInCompanyValidation } from '../../../../../shared-components/contracts-in-company/contracts-in-company.component';
import { RouteAuthorizationService } from '../../../../../services/authorization/route-authorization.service';
import { CustomModalService } from '../../../../../shared-components/custom-modal/custom-modal.service';

@Component({
	selector: 'app-documents-upload-step1',
	templateUrl: './documents-upload-step1.component.html',
	styleUrls: ['../documents-upload.scss'],
	standalone: false,
})
export class DocumentsUploadStep1Component implements OnInit {
	public isPerformingRequest = false;
	protected uploadContractType: DocumentUploadContractType | null = null;
	protected fileRelatedContractId: string = '';
	private loggedUser: User | null = new User();
	protected companyId: string = '';
	protected contractsInCompanyValidation: ContractsInCompanyValidation | null = null;

	currentStep: number = 0;
	steps: Step[] = [];

	constructor(
		private serverApiService: ServerApiService,
		private logger: LogService,
		private toasterService: ToasterService,
		private utilitiesService: UtilitiesService,
		private route: ActivatedRoute,
		private appContext: AppContextService,
		private router: Router,
		private modalService: CustomModalService,
		private translateService: TranslateService,
		private routeAuthorizationService: RouteAuthorizationService
	) {
		this.initializeSteps();
	}

	initializeSteps(): void {
		this.steps = [
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step1Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step2Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step3Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step4Label', 'client'),
				icon: 'fa-solid fa-check',
			},
		];
	}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (this.canAccessStep1()) {
				this.route.params.subscribe((params) => {
					const uploadContractTypeRouteParam = params['uploadContractType'] || '';
					if (!!uploadContractTypeRouteParam) {
						this.uploadContractType = DocumentUploadContractTypeExtensions.FromRouteParam(uploadContractTypeRouteParam);
					}

					if (!this.uploadContractType) {
						this.logger.error('Missing uploadContractType on documents upload step1.');
						this.toasterService.showError(
							this.translateService.translate('documents-upload-step1.missing-uploadcontracttype-on-documents-upload-step1', 'client')
						);
						return;
					}

					this.route.queryParams.subscribe((params) => {
						this.fileRelatedContractId = params['contractId'] || '';

						// Show instructions modal when no contract is preselected
						if (!this.fileRelatedContractId) {
							setTimeout(() => this.openInstructionsModal(), 0);
						}
					});
				});
			} else {
				this.routeAuthorizationService.routeOnUnauthorized();
			}
		});
	}

	protected downloadTemplate() {
		this.isPerformingRequest = true;
		this.serverApiService.generateDocumentRecordsUploadTemplate(this.uploadContractType!).subscribe({
			next: (operationResult: OperationResult<DocumentRecordsUploadTemplateResult>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					this.processValidDocumentRecordsUploadTemplateResult(operationResult);
				} else {
					this.processInvalidResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error('[DocumentsUploadStep1Component::generateDocumentRecordsUploadTemplate] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showError(
					'[DocumentsUploadStep1Component::generateDocumentRecordsUploadTemplate] HttpErrorResponse: ' +
						httpErrorResponse.error +
						httpErrorResponse.message
				);
			},
		});
	}

	private processInvalidResult<T>(operationResult: OperationResult<T>) {
		const errors = this.utilitiesService.GetDistinctOperationErrors(operationResult.Errors);

		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
	}

	private processValidDocumentRecordsUploadTemplateResult(operationResult: OperationResult<DocumentRecordsUploadTemplateResult>) {
		const blob = this.utilitiesService.base64ToBlob(operationResult.Data.Content, operationResult.Data.ContentType);

		//strategy from https://stackoverflow.com/questions/19327749/javascript-blob-filename-without-link
		const element = document.createElement('a');
		element.href = URL.createObjectURL(blob);
		element.download = operationResult.Data.ContentName;
		document.body.appendChild(element);
		element.click();
	}

	private formIsValid(): boolean {
		return !!(this.contractsInCompanyValidation?.isCompanyValid && this.contractsInCompanyValidation?.isContractValid);
	}

	protected redirectToDocumentsStep2(): void {
		if (this.formIsValid()) {
			this.router.navigate([
				'/private/files-upload/upload/documents/step2',
				DocumentUploadContractTypeExtensions.ToRouteParam(this.uploadContractType!),
				this.fileRelatedContractId,
			]);
		}
	}

	protected showConfirmingUploadInstructions(): boolean {
		return this.uploadContractType === DocumentUploadContractType.Confirming;
	}

	protected showFactoringUploadInstructions(): boolean {
		return this.uploadContractType === DocumentUploadContractType.Factoring;
	}

	protected hasEnteredRequiredData(): boolean {
		return !!this.uploadContractType && !!this.companyId && !!this.fileRelatedContractId;
	}

	protected onCompanyChange(companyId: string): void {
		this.companyId = companyId;
	}

	protected onContractChange(contractId: string): void {
		this.fileRelatedContractId = contractId;
	}

	protected onValidationChange(validation: ContractsInCompanyValidation): void {
		this.contractsInCompanyValidation = validation;
	}

	protected openInstructionsModal(): void {
		const instructionsModalRef = this.modalService.open<string, { uploadContractType: DocumentUploadContractType | null }>({
			size: 'lg',
			title: this.translateService.translate('documents-upload-instructions-modal.title', 'client'),
			body: {
				kind: 'component',
				componentType: DocumentsUploadInstructionsModalComponent,
			},
			actions: [
				{
					id: 'start',
					label: this.translateService.translate('button.start', 'client') || 'Start',
					color: 'primary',
					variant: 'outlined',
					autoFocus: true,
					run: ({ bodyComponent, modalRef }) => {
						const result = bodyComponent?.start();
						modalRef.close(result);
					},
				},
			],
			data: {
				uploadContractType: this.uploadContractType,
			},
		});

		instructionsModalRef.result.catch(() => undefined);
	}

	private canAccessStep1(): boolean {
		return (
			!!this.loggedUser &&
			!this.loggedUser.IsBackOfficeOperator &&
			(this.loggedUser.canUploadDocumentsForConfirming() || this.loggedUser.canUploadDocumentsForFactoring())
		);
	}

	protected redirectToDocuments(): void {
		this.router.navigate(['/private/documents']);
	}
}
